"use client";
import React, { useState } from "react";
import dynamic from "next/dynamic";
import { ChevronDownIcon } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FaqSkeleton } from "./components/loader";
import { Button } from "@/components/ui/button";
import { ArrowDownLoad } from "@/components/icons";

const VideoPlayer = dynamic(() => import("../video-player"), { ssr: false });

const videoJsOptions = {
  autoplay: false,
  controls: true,
  responsive: true,
  fluid: true,
  aspectRatio: "16:9",
};

const FaqItem = ({ faq, index }: { faq: any; index: number }) => {
  return (
    <AccordionItem
      value={String(index)}
      className="my-4 cursor-default rounded-[20px] border-none bg-[#F5F7FA] px-6 py-2 shadow-none hover:bg-[#F5F7FA] data-[state=open]:bg-[#F5F7FA] lg:px-8"
    >
      <AccordionTrigger
        className="px-0 text-xl font-semibold text-primary outline-none hover:no-underline lg:text-4xl leading-tight lg:leading-tight"
        icon={
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#E8EEF5] text-slate-700 transition-all duration-200 select-none group-data-[state=open]:bg-slate-900 group-data-[state=open]:text-white lg:size-12">
            <ChevronDownIcon className="size-5 transition-transform duration-300 group-data-[state=open]:rotate-180 lg:size-6" />
          </div>
        }
      >
        {faq?.question}
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-4 px-0 pb-6">
        <div
          className="text-base leading-tight font-normal whitespace-pre-line text-slate-800 lg:text-3xl lg:leading-tight [&_a]:text-blue-600 [&_a]:underline [&_a]:transition-colors hover:[&_a]:text-blue-800 [&_h1]:my-6 [&_h1]:text-3xl [&_h1]:font-semibold [&_h1]:text-slate-800 lg:[&_h1]:text-6xl [&_h2]:my-4 [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-slate-800 lg:[&_h2]:text-5xl [&_h3]:my-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-slate-800 lg:[&_h3]:text-4xl [&_li]:my-1 [&_li]:text-slate-800 [&_ol]:list-decimal [&_ol]:pl-6 lg:[&_ol]:pl-10 [&_p]:my-2 [&_p]:text-slate-800 [&_strong]:font-semibold [&_strong]:text-slate-800 [&_ul]:list-disc [&_ul]:pl-6 lg:[&_ul]:pl-10 [&_li[data-list=bullet]]:list-disc! [&_li[data-list=ordered]]:list-decimal!"
          dangerouslySetInnerHTML={{ __html: faq?.answer || "" }}
        />
        {faq?.video_link && (
          <div className="mt-4 aspect-video w-full">
            <VideoPlayer
              options={{
                ...videoJsOptions,
                sources: [
                  {
                    src: faq?.video_link,
                    type: "video/youtube",
                  },
                ],
              }}
            />
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

const Faq = ({ data: faqs, isLoading }: { data: any; isLoading: boolean }) => {
  const [showMore, setShowMore] = useState<boolean>(false);
  const shortFaqList = faqs?.slice(0, 5) || [];
  const extraFaqList = showMore ? faqs?.slice(5) || [] : [];

  if (isLoading) return <FaqSkeleton />;

  return (
    <section
      id="faq"
      className="container grid grid-cols-1 gap-x-16 gap-y-8 py-12 lg:grid-cols-[33%_67%] lg:py-32"
    >
      <div className="flex flex-col gap-4 lg:gap-8">
        <h3 className="text-primary leading-tight text-3xl font-semibold lg:text-7xl">
          Frequently{" "}
          <span className="font-vollkorn font-normal whitespace-nowrap text-slate-900 italic">
            Asked Questions
          </span>
        </h3>
        <p className="max-w-xl text-base leading-tight font-medium text-slate-800 lg:text-3xl">
          Find answers to some of the most common questions about the Basic
          Training.
        </p>
      </div>
      <div>
        <div>
          <Accordion type="single" collapsible defaultValue="0">
            {shortFaqList?.map((faq: any, index: number) => (
              <FaqItem key={faq?.id || index} faq={faq} index={index} />
            ))}
            {showMore &&
              extraFaqList.map((faq: any, index: number) => (
                <FaqItem
                  key={faq?.id || index + 5}
                  faq={faq}
                  index={index + 5}
                />
              ))}
          </Accordion>
        </div>
        {faqs?.length > 5 && (
          <div className="flex items-center justify-center">
            <Button
              className="mt-2 w-full rounded-full lg:mt-4 lg:w-fit"
              size="2xl"
              onClick={() => setShowMore(!showMore)}
            >
              {showMore ? "Show Less" : "Show More"} FAQs
              {showMore ? (
                <div className="rotate-180">
                  <ArrowDownLoad className="size-6" />
                </div>
              ) : (
                <ArrowDownLoad className="size-6" />
              )}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Faq;
